import { ChangeDetectorRef, Provider, inject } from '@angular/core';

import { HydrationTrackerService, PLATFORM } from '@frontend/vanilla/ssr';
import {
    RX_CONCURRENT_STRATEGIES,
    RX_RENDER_STRATEGIES_CONFIG,
    RxConcurrentStrategyNames,
    RxStrategyCredentials,
} from '@rx-angular/cdk/render-strategies';
import { switchMap, tap } from 'rxjs';

/**
 * This function runs on the client side and provides RxRenderStrategies with hydration support.
 *
 * By default, when using RxAngular directives, the default render strategy is `normal`, which is a concurrent strategy.
 *
 * When we hydrate the app, we don't want to use the concurrent strategy on initial render because it will cause a flash of unhydrated content.
 *
 * So we check if the app is fully hydrated and if it is, we use the concurrent strategy, otherwise we use the `markForCheck` strategy.
 *
 * @returns Provider
 */
export const provideRxRenderStrategiesWithHydrationSupport = (): Provider => ({
    provide: RX_RENDER_STRATEGIES_CONFIG,
    useFactory: () => {
        const hydrationTracker = inject(HydrationTrackerService);
        const platform = inject(PLATFORM);
        const strategyFactory = (name: RxConcurrentStrategyNames) => ({
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            [name]: {
                name,
                work: platform.isServerRendered
                    ? hydrationTracker.isFullyHydrated()
                        ? RX_CONCURRENT_STRATEGIES[name].work
                        : (cdRef: ChangeDetectorRef) => cdRef.markForCheck()
                    : RX_CONCURRENT_STRATEGIES[name].work,
                behavior:
                    ({ work, scope, ngZone }) =>
                    (o$) =>
                        platform.isServerRendered
                            ? o$.pipe(
                                  switchMap(() =>
                                      hydrationTracker.isFullyHydrated$.pipe((hydrated$) =>
                                          hydrationTracker.isFullyHydrated()
                                              ? RX_CONCURRENT_STRATEGIES[name].behavior({ work, scope, ngZone })(hydrated$)
                                              : hydrated$.pipe(tap(work)),
                                      ),
                                  ),
                              )
                            : RX_CONCURRENT_STRATEGIES[name].behavior,
            } as RxStrategyCredentials,
        });
        return {
            customStrategies: {
                ...(['immediate', 'userBlocking', 'normal', 'low', 'idle'] as RxConcurrentStrategyNames[]).reduce(
                    (previousValue, currentValue) => ({ ...previousValue, ...strategyFactory(currentValue) }),
                    {},
                ),
            },
        };
    },
});
